/**
 * MasterDetail - responsive master/detail layout.
 *
 * Desktop: side-by-side (224px list + flex-1 detail).
 * Mobile: full-width list; when an item is selected, detail replaces list with a back button.
 */

import { ArrowLeft, Plus } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import { Button } from '@/shared/components/ui/button'

interface MasterDetailProps {
  /** Section title displayed above the list */
  title?: string
  /** The list/sidebar content */
  list: ReactNode
  /** The detail/content panel */
  detail: ReactNode
  /** Whether an item is currently selected (controls mobile view toggle) */
  hasSelection: boolean
  /** Called when user taps "Back" on mobile detail view */
  onBack: () => void
  /** When provided, renders a primary "+" button next to the title */
  onAdd?: () => void
  /** Optional minimum height - the container will grow with content. */
  minHeight?: string
  /**
   * Optional fixed height - when set, the list and detail panels scroll
   * independently within this height. Use this instead of minHeight when
   * the list can contain many items (hundreds+) to avoid the whole page
   * scrolling.
   */
  height?: string
  /** Optional test ID prefix - generates {testId}-list and {testId}-detail */
  testId?: string
  /** Optional data-tour attribute applied to the list panel only. */
  dataTourList?: string
  /** Optional selected item id for sidebar-controlled layouts */
  selectedId?: string | null
  /** Optional visible item ids in the current sidebar order */
  visibleItemIds?: string[]
  /** Optional selection setter used by MasterDetail-owned behaviors */
  onSelectionChange?: (id: string | null) => void
}

function ListHeader({ title, onAdd, testId }: Pick<MasterDetailProps, 'title' | 'onAdd' | 'testId'>) {
  if (!title) return null
  return (
    <div className="mb-4 flex items-center gap-2">
      <span className="clara-section-label">{title}</span>
      {onAdd && (
        <Button
          variant="ghost"
          size="icon-xs"
          data-testid={testId ? `${testId}-add-btn` : undefined}
          onClick={onAdd}
          className="h-5 w-5 cursor-pointer rounded-[4px] bg-[var(--green)] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] hover:text-[var(--bg)]"
        >
          <Plus className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}

export function MasterDetail({
  title,
  list,
  detail,
  hasSelection,
  onBack,
  onAdd,
  minHeight = 'calc(100vh - 56px - 64px)',
  height,
  testId,
  dataTourList,
  selectedId,
  visibleItemIds,
  onSelectionChange,
}: MasterDetailProps) {
  // Dynamic prop values - must use style (Tailwind can't generate arbitrary
  // class names at runtime). This is the allowed exception per CLAUDE.md.
  const sizeStyle = height ? { height } : { minHeight }
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(min-width: 1024px)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  useEffect(() => {
    if (!isDesktop || !visibleItemIds || !onSelectionChange) return

    if (visibleItemIds.length === 0) {
      if (selectedId !== null && selectedId !== undefined) {
        onSelectionChange(null)
      }
      return
    }

    if (!selectedId || !visibleItemIds.includes(selectedId)) {
      onSelectionChange(visibleItemIds[0])
    }
  }, [isDesktop, onSelectionChange, selectedId, visibleItemIds])

  return (
    <>
      {/* Render only the layout in use. Both used to be in the DOM with one
          hidden by CSS, so every list item existed twice until auto-select
          swapped the mobile list out - long enough for a lookup by test id to
          find two matches (the flaky OC Claim Schemas e2e). */}
      {/* Desktop: side-by-side */}
      {isDesktop && (
        <div
          className="hidden overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] lg:flex lg:gap-0"
          style={sizeStyle}
          data-testid={testId}
        >
          <div
            className="w-60 shrink-0 overflow-y-auto border-r-[0.5px] border-r-[var(--rule)] px-4 py-4"
            data-testid={testId ? `${testId}-list` : undefined}
            data-tour={dataTourList}
          >
            <ListHeader title={title} onAdd={onAdd} testId={testId} />
            {list}
          </div>
          <div
            className="flex-1 overflow-y-auto px-6 py-4"
            data-testid={testId ? `${testId}-detail` : undefined}
          >
            {detail}
          </div>
        </div>
      )}

      {/* Mobile: toggle between list and detail */}
      {!isDesktop && (
        <div
          className="overflow-y-auto rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] lg:hidden"
          style={sizeStyle}
        >
          {!hasSelection ? (
            <div className="p-4" data-tour={dataTourList}>
              <ListHeader title={title} onAdd={onAdd} testId={testId} />
              {list}
            </div>
          ) : (
            <div className="p-4">
              <Button
                variant="ghost"
                size="xs"
                onClick={onBack}
                className="mb-4 gap-1 text-xs text-[var(--ink-3)]"
              >
                <ArrowLeft className="size-3" />
                Back to list
              </Button>
              {detail}
            </div>
          )}
        </div>
      )}
    </>
  )
}
